import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@clerk/nextjs'
import type { MeSchoolUser } from '@school-journey/shared-types/api/users'
import { getMe } from '@/lib/api/users/me'
import { useBackendClient } from '@/lib/backend-client'

/**
 * School membership from the /v1/users/me response.
 * Each entry represents the user's role at a specific school.
 */
export type MySchoolMembership = MeSchoolUser

export const MY_SCHOOLS_QUERY_KEY = ['my-schools'] as const

/**
 * Fetches the current user's school memberships from GET /v1/users/me.
 * Returns the schoolUsers array with role and status info for each school.
 * Does NOT require active school context (uses setupAuthMiddleware).
 */
export function useMySchools() {
  const { userId } = useAuth()
  const client = useBackendClient()

  const { data, isLoading, isError } = useQuery({
    queryKey: [...MY_SCHOOLS_QUERY_KEY, userId],
    queryFn: async () => (await getMe(client)).data.schoolUsers,
    enabled: !!userId,
    staleTime: 5 * 60 * 1000
  })

  return {
    schoolMemberships: data ?? [],
    isLoading,
    isError
  }
}
